import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Upload as UploadIcon, CheckCircle, MessageCircle, Trash2 as Trash2Icon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Upload from 'rc-upload';
import DeleteFileButton from './DeleteFileButton';
import { useAlert } from '../hooks/useAlert';
import {
  uploadEscritura,
  deleteEscritura
} from '../services/tramiteService';

import { storageDownload } from '../services/storageService';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { MAX_ESCRITURA_FILE_SIZE_BYTES, MAX_ESCRITURA_FILE_SIZE_MB } from '@/common/constants';

const UploadDownloadEscritura = ({ tramite: tramiteProp, publicada = false, onChange, canSendWhatsApp = false, onSendWhatsApp = null, inlineConfirm = false }) => {
  const [tramite, setTramite] = useState(tramiteProp);
  const [loading, setLoading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { errorAlert, successAlert } = useAlert();
  const fieldName = publicada ? 'escritura_pub_path' : 'escritura_path';
  const isDownload = !!tramite[fieldName];
  const { canUploadEscritura } = useAuthorization();

  useEffect(() => {
    if (tramiteProp !== tramite) {
      onChange && onChange()
    }
  }, [tramite]);

  const handleDownload = () => {
    setLoading(true);
    storageDownload({ path: tramite[fieldName] })
      .finally(() => setLoading(false));

  }

  const doDelete = () => {
    setConfirmingDelete(false);
    setLoading(true);
    deleteEscritura({
      idTramite: tramite.id,
      publicada
    }).then(r => {
      successAlert({ message: 'Escritura borrada con éxito' });
      setTramite(r);
    }).catch(err => {
      errorAlert({
        title: 'Error al borrar escritura',
        exception: err
      });
    }).finally(() => {
      setLoading(false);
    })
  }

  const handleDelete = () => {
    if (inlineConfirm) {
      setConfirmingDelete(true);
    } else {
      doDelete();
    }
  }

  const validateFileSize = (file) => {
    if (file.size > MAX_ESCRITURA_FILE_SIZE_BYTES) {
      errorAlert({
        title: 'No se pudo subir el archivo',
        message: `El archivo supera el tamaño máximo permitido de ${MAX_ESCRITURA_FILE_SIZE_MB}MB`
      });
      return false;
    }
    return true;
  }

  const isFileSizeError = (err) => {
    const status = err?.response?.status;
    if (status === 413) return true; // rejected by post_max_size / LimitRequestBody
    const fieldErrors: string[] = err?.response?.data?.errors?.escritura || [];
    return status === 422 && fieldErrors.some(msg => msg.includes('MB'));
  }

  const handleUpload = (file) => {
    //On click upload
    setLoading(true);
    uploadEscritura({
      idTramite: tramite.id,
      file,
      publicada
    }).then(r => {
      successAlert({ message: 'Subió escritura con éxito' });
      setTramite(r);
    }).catch(err => {
      if (isFileSizeError(err)) {
        errorAlert({
          title: 'Archivo demasiado grande',
          message: `El archivo supera el tamaño máximo permitido de ${MAX_ESCRITURA_FILE_SIZE_MB}MB`
        });
      } else {
        errorAlert({
          title: 'Error al subir escritura',
          exception: err
        });
      }
    }).finally(() => {
      setLoading(false);
    });

  }



  return (
    <div className="flex flex-wrap items-center justify-between gap-y-1 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
      {/* Label */}
      <div className="flex items-center gap-2 min-w-0">
        {isDownload && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
        <span className="text-sm font-medium text-gray-700 truncate">
          Escritura {publicada ? 'inscripta' : ''}
        </span>
      </div>

      {/* Actions — in-row confirm cuando corresponde */}
      <div className="flex items-center gap-2 flex-shrink-0 ml-auto pl-2">
        {confirmingDelete ? (
          <>
            <span className="text-xs text-red-600 font-medium whitespace-nowrap">¿Eliminar?</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(false)}
              disabled={loading}
              className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-100"
            >
              No
            </Button>
            <Button
              size="sm"
              onClick={doDelete}
              disabled={loading}
              className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? '...' : 'Sí'}
            </Button>
          </>
        ) : (
          <TooltipProvider>
            {!isDownload && canUploadEscritura && (
              <Upload multiple={false} beforeUpload={validateFileSize} action={handleUpload}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={loading}
                      className="h-8 w-8 p-0 hover:bg-blue-100"
                    >
                      <UploadIcon className="w-4 h-4 text-blue-600" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Subir escritura {publicada ? 'inscripta' : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </Upload>
            )}

            {isDownload && (
              <>
                {canSendWhatsApp && typeof onSendWhatsApp === 'function' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={loading}
                        onClick={onSendWhatsApp}
                        className="h-8 w-8 p-0 hover:bg-green-100"
                      >
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Enviar escritura {publicada ? 'inscripta ' : ''}por WhatsApp</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={loading}
                      onClick={handleDownload}
                      className="h-8 w-8 p-0 hover:bg-green-100"
                    >
                      <Download className="w-4 h-4 text-green-600" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Descargar escritura {publicada ? 'inscripta' : ''}</p>
                  </TooltipContent>
                </Tooltip>

                {canUploadEscritura && (
                  inlineConfirm ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={loading}
                          onClick={handleDelete}
                          className="h-8 w-8 p-0 hover:bg-red-100"
                        >
                          <Trash2Icon className="w-4 h-4 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Eliminar escritura {publicada ? 'inscripta' : ''}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DeleteFileButton
                          title={`¿Seguro desea eliminar la escritura ${publicada ? 'inscripta' : ''}?`}
                          onDeleteConfirm={doDelete}
                          disabled={loading}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-red-100"
                          iconSize={16}
                          iconClassName="text-red-600"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Eliminar escritura {publicada ? 'inscripta' : ''}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                )}
              </>
            )}
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

export default UploadDownloadEscritura;
